import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDistanceMatrix } from "./distanceMatrix";
import { type ActivityItem as ActivityItemType } from "@/types";
import { motion } from "framer-motion";

interface DividerProps {
  prevItem: ActivityItemType;
  nextItem: ActivityItemType;
  locationName: string;
  dayItems: ActivityItemType[];
}

interface DistanceInfo {
  distance: string;
  duration: string;
}

interface PairData {
  walking: DistanceInfo;
  driving: DistanceInfo;
  transit: DistanceInfo;
}

type TransportMode = "driving" | "walking" | "transit";

const transportModes: {
  [key in TransportMode]: { icon: string; label: string };
} = {
  driving: { icon: "fa-car", label: "Driving" },
  walking: { icon: "fa-walking", label: "Walking" },
  transit: { icon: "fa-train-subway", label: "Transit" },
};

const Divider: React.FC<DividerProps> = ({
  prevItem,
  nextItem,
  locationName,
  dayItems,
}) => {
  const [hoveredMode, setHoveredMode] = useState<TransportMode | null>(null);
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useDistanceMatrix(dayItems);

  const getItemId = (item: ActivityItemType): string => {
    return "id" in item ? item.id.toString() : "";
  };

  const pairKey = `${getItemId(prevItem)}-${getItemId(nextItem)}`;
  const pairData =
    queryClient.getQueryData<PairData>(["distance", pairKey]) ||
    data?.[pairKey];

  const isPairLoading = !pairData && (isLoading || isFetching);

  const renderModeInfo = (mode: TransportMode) => {
    const modeData = pairData?.[mode];
    const isHovered = hoveredMode === mode;

    // Don't render anything if modeData is null or undefined
    if (modeData?.distance === "" || modeData?.duration === "") return null;

    return (
      <div key={mode} className="flex flex-row items-center space-x-2">
        <i
          className={`fa-solid ${transportModes[mode].icon} fa-fw text-[14px] mr-1 text-neutral-500 duration-300 ease-in-out transition`}
        ></i>
        <p className="text-[10px] font-normal text-neutral-500 w-auto whitespace-nowrap duration-300 ease-in-out transition">
          {modeData?.duration}
        </p>
        <p className="text-neutral-500 font-bold text-[10px] pr-1 duration-300 ease-in-out transition">
          •
        </p>
        <p className="text-[10px] font-normal text-neutral-500 w-auto whitespace-nowrap duration-300 ease-in-out transition">
          {modeData?.distance}
        </p>
      </div>
    );
  };

  const renderedModes = Object.keys(transportModes)
    .map((mode) => renderModeInfo(mode as TransportMode))
    .filter(Boolean);

  if (!pairData && !isPairLoading)
    return (
      <div className="activity-divider pl-3 py-3 flex flex-row items-center gap-3 justify-center">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="bg-neutral-300 h-1 w-1 rounded-full"></div>
        ))}
      </div>
    );

  return (
    <div className="activity-divider pl-3 py-3 flex flex-row items-center gap-3 justify-center">
      {isPairLoading ? (
        Array.from({ length: 3 }, (_, i) => (
          <motion.div
            key={i}
            className="h-1 w-1 rounded-full"
            initial={{ backgroundColor: "rgb(163 163 163)" }}
            animate={{
              backgroundColor: [
                "rgb(163 163 163)",
                "rgb(38 38 38)",
                "rgb(163 163 163)",
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut",
            }}
          />
        ))
      ) : (
        <div className="space-x-8 flex flex-row items-center">
          {renderedModes}
        </div>
      )}
    </div>
  );
};

export default Divider;
